"use client";

import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getCompanyById, searchCompaniesByName, getIndustryOptions, getCompanyTypeOptions, getOwners } from '@/lib/hubspot/actions';
import { HubSpotCompany, HubSpotPropertyOption, HubSpotOwner } from '@/lib/hubspot/types';

export default function HubSpotTestPage() {
  const [companies, setCompanies] = useState<HubSpotCompany[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<HubSpotCompany | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [searchName, setSearchName] = useState('');
  const [industryOptions, setIndustryOptions] = useState<HubSpotPropertyOption[]>([]);
  const [typeOptions, setTypeOptions] = useState<HubSpotPropertyOption[]>([]);
  const [owners, setOwners] = useState<HubSpotOwner[]>([]);
  
  useEffect(() => {
    const loadOwners = async () => {
      try {
        const ownersList = await getOwners();
        setOwners(ownersList);
      } catch (error) {
        console.error("Error loading owners:", error);
      }
    };
    void loadOwners();
  }, []);

  const getOwnerName = (id?: string) => {
    if (!id) return '-';
    const owner = owners.find(o => o.id === id);
    return owner ? `${owner.firstName} ${owner.lastName}` : id;
  };

  const handleSearch = async () => {
    if (searchName.trim().length < 2) return;
    setLoading(true);
    setSelectedCompany(null);
    try {
      const results = await searchCompaniesByName(searchName);
      setCompanies(results);
    } catch (error) {
      console.error(error);
      alert('Error buscando compañías');
    }
    setLoading(false);
  };

  const handleSelectCompany = async (companyId: string) => {
    setLoadingDetails(true);
    try {
      setSelectedCompany(await getCompanyById(companyId));
    } catch (error) {
      console.error(error);
      alert('Error cargando el detalle de la compania');
    }
    setLoadingDetails(false);
  };

  const handleGetOptions = async () => {
    setLoading(true);
    try {
      const [industries, types] = await Promise.all([
        getIndustryOptions(),
        getCompanyTypeOptions()
      ]);
      setIndustryOptions(industries);
      setTypeOptions(types);
    } catch (error) {
      console.error(error);
      alert('Error obteniendo opciones');
    }
    setLoading(false);
  };

  return (
    <div className="p-8 space-y-6 max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold mb-6">Test Integración HubSpot</h1>
      
      <Tabs defaultValue="read" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="read">Lectura y Búsqueda</TabsTrigger>
          <TabsTrigger value="options">Opciones (Sectores/Tipos)</TabsTrigger>
        </TabsList>

        { }
        <TabsContent value="read" className="space-y-4">
          <Card>
            <CardContent className="p-6 space-y-4">
              <div className="flex gap-2">
                <div className="flex gap-2 flex-1">
                  <Input 
                    placeholder="Buscar por nombre..." 
                    value={searchName}
                    onChange={(e) => setSearchName(e.target.value)}
                  />
                  <Button variant="secondary" onClick={() => void handleSearch()} disabled={loading || searchName.trim().length < 2}>
                    Buscar
                  </Button>
                </div>
              </div>

              <Separator />

              <div className="bg-slate-50 p-4 rounded-md min-h-[200px] max-h-[500px] overflow-auto">
                {companies.length === 0 ? (
                  <p className="text-muted-foreground text-center py-10">No hay resultados</p>
                ) : (
                  <ul className="space-y-3">
                    {companies.map((company) => (
                      <li key={company.id} className="bg-white p-3 rounded shadow-sm border text-sm">
                        <div className="font-bold text-lg">{company.displayName || company.properties.name || company.properties.domain || `Empresa ${company.id}`}</div>
                        <div className="grid grid-cols-2 gap-2 mt-2 text-slate-600">
                          <div><span className="font-semibold">ID:</span> {company.id}</div>
                          <div><span className="font-semibold">Owner:</span> {getOwnerName(company.properties.hubspot_owner_id)}</div>
                          <div><span className="font-semibold">Dominio:</span> {company.properties.domain || '-'}</div>
                          <div><span className="font-semibold">Ciclo:</span> {company.properties.lifecyclestage || '-'}</div>
                          <div><span className="font-semibold">Riesgo:</span> {company.properties.riesgo || '-'}</div>
                        </div>
                        <Button className="mt-3" variant="secondary" onClick={() => void handleSelectCompany(company.id)} disabled={loadingDetails}>
                          {loadingDetails ? 'Cargando detalle...' : 'Ver detalle H1'}
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {selectedCompany && (
                <div className="border-t pt-4">
                  <h2 className="font-semibold">Detalle H1: {selectedCompany.properties.name || selectedCompany.id}</h2>
                  <div className="grid grid-cols-2 gap-2 mt-2 text-sm text-slate-600">
                    <div><span className="font-semibold">Razon social:</span> {selectedCompany.properties.razon_social || '-'}</div>
                    <div><span className="font-semibold">CIF:</span> {selectedCompany.properties.cif || '-'}</div>
                    <div><span className="font-semibold">Telefono:</span> {selectedCompany.properties.phone || '-'}</div>
                    <div><span className="font-semibold">Direccion:</span> {selectedCompany.properties.address || '-'}</div>
                    <div><span className="font-semibold">Ciudad:</span> {selectedCompany.properties.city || '-'}</div>
                    <div><span className="font-semibold">Provincia:</span> {selectedCompany.properties.provincia || '-'}</div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        { }
        <TabsContent value="options" className="space-y-4">
          <Card>
            <CardContent className="p-6 space-y-4">
              <Button onClick={() => void handleGetOptions()} disabled={loading}>
                {loading ? 'Cargando...' : 'Cargar Opciones de HubSpot'}
              </Button>

              <div className="grid grid-cols-3 gap-6 mt-4">
                <div>
                  <h3 className="font-bold mb-2">Sectores (Industry)</h3>
                  <div className="bg-slate-50 p-4 rounded-md h-[400px] overflow-auto border">
                    {industryOptions.length === 0 ? <p className="text-muted-foreground">Vacío</p> : (
                      <ul className="list-disc pl-4 space-y-1">
                        {industryOptions.map((opt) => (
                          <li key={opt.value} className="text-sm">
                            <span className="font-semibold">{opt.label}</span> <span className="text-xs text-muted-foreground">({opt.value})</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="font-bold mb-2">Tipos de Compañía</h3>
                  <div className="bg-slate-50 p-4 rounded-md h-[400px] overflow-auto border">
                    {typeOptions.length === 0 ? <p className="text-muted-foreground">Vacío</p> : (
                      <ul className="list-disc pl-4 space-y-1">
                        {typeOptions.map((opt) => (
                          <li key={opt.value} className="text-sm">
                            <span className="font-semibold">{opt.label}</span> <span className="text-xs text-muted-foreground">({opt.value})</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="font-bold mb-2">Propietarios (Owners)</h3>
                  <div className="bg-slate-50 p-4 rounded-md h-[400px] overflow-auto border">
                    {owners.length === 0 ? <p className="text-muted-foreground">Cargando...</p> : (
                      <ul className="space-y-2">
                        {owners.map((owner) => (
                          <li key={owner.id} className="text-sm bg-white p-2 rounded border">
                            <div className="font-semibold">{owner.firstName} {owner.lastName}</div>
                            <div className="text-xs text-muted-foreground">{owner.email}</div>
                            <div className="text-xs text-gray-400">ID: {owner.id}</div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
